import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { seoCollectionAPI, placeAPI, locationAPI, authorsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, GripVertical, Search, Eye, EyeOff, Globe, FileText, ArrowUp, ArrowDown, X, MapPin, Image as ImageIcon, ExternalLink, Lightbulb, LayoutList, Map } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";

const categoryIcons = { Restaurants: "🍽️", Temples: "🛕", Beaches: "🏖️", Waterfalls: "💦", Forts: "🏰", Parks: "🌳" };

const GUIDE_CATEGORIES = [
  { value: "general", label: "General" },
  { value: "temple", label: "Temple Guide" },
  { value: "beach", label: "Beach Guide" },
  { value: "fort", label: "Fort Guide" },
];

const ICON_OPTIONS = [
  "location", "deity", "timings", "fee", "season", "dress",
  "distance", "bus", "train", "difficulty", "elevation",
  "info", "camera", "footwear", "phone", "ideal", "water", "built",
];

const CATEGORY_TEMPLATES = {
  temple: {
    quick_info: [
      { label: "Location", value: "", icon: "location" },
      { label: "Main Deity", value: "", icon: "deity" },
      { label: "Timings", value: "", icon: "timings" },
      { label: "Entry Fee", value: "", icon: "fee" },
      { label: "Best Time", value: "", icon: "season" },
      { label: "Dress Code", value: "", icon: "dress" },
    ],
    sections: [
      { title: "History", slug: "history", content: "", image: "", order: 0 },
      { title: "Architecture", slug: "architecture", content: "", image: "", order: 1 },
      { title: "Darshan & Aarti Timings", slug: "darshan-aarti-timings", content: "", image: "", order: 2 },
      { title: "Festivals Celebrated", slug: "festivals-celebrated", content: "", image: "", order: 3 },
      { title: "Temple Rules", slug: "temple-rules", content: "", image: "", order: 4 },
      { title: "How to Reach", slug: "how-to-reach", content: "", image: "", order: 5 },
    ],
    travel_tips: [
      "Visit early morning for a peaceful darshan without crowds",
      "Carry appropriate clothing as per temple dress code",
      "Remove footwear before entering the temple premises",
      "Photography may be restricted inside the sanctum",
    ],
  },
  beach: {
    quick_info: [
      { label: "Destination", value: "", icon: "location" },
      { label: "Beaches Covered", value: "", icon: "water" },
      { label: "Distance", value: "", icon: "distance" },
      { label: "Best Time", value: "", icon: "season" },
      { label: "Ideal For", value: "", icon: "ideal" },
      { label: "Water Sports", value: "", icon: "water" },
    ],
    sections: [
      { title: "Best Time to Visit", slug: "best-time-to-visit", content: "", image: "", order: 0 },
      { title: "Water Sports & Activities", slug: "water-sports-activities", content: "", image: "", order: 1 },
      { title: "How to Reach", slug: "how-to-reach", content: "", image: "", order: 2 },
      { title: "Things to Do", slug: "things-to-do", content: "", image: "", order: 3 },
    ],
    travel_tips: [
      "Visit during October to February for the best weather",
      "Carry sunscreen, hat, and sunglasses for sun protection",
      "Check tide timings before swimming or water sports",
      "Wear comfortable footwear suitable for sandy beaches",
    ],
  },
  fort: {
    quick_info: [
      { label: "Location", value: "", icon: "location" },
      { label: "Built By", value: "", icon: "built" },
      { label: "Elevation", value: "", icon: "elevation" },
      { label: "Trek Difficulty", value: "", icon: "difficulty" },
      { label: "Entry Fee", value: "", icon: "fee" },
      { label: "Best Season", value: "", icon: "season" },
    ],
    sections: [
      { title: "History", slug: "history", content: "", image: "", order: 0 },
      { title: "Architecture & Structures", slug: "architecture-structures", content: "", image: "", order: 1 },
      { title: "Trek Information", slug: "trek-information", content: "", image: "", order: 2 },
      { title: "Things to See", slug: "things-to-see", content: "", image: "", order: 3 },
      { title: "How to Reach", slug: "how-to-reach", content: "", image: "", order: 4 },
    ],
    travel_tips: [
      "Start the trek early morning to avoid heat",
      "Carry at least 2 liters of water per person",
      "Wear sturdy trekking shoes with good grip",
      "Do not trek during heavy monsoon rains",
    ],
  },
};

const AdminSeoCollections = () => {
  const { token } = useAuth();
  const [collections, setCollections] = useState([]);
  const [allPlaces, setAllPlaces] = useState([]);
  const [locations, setLocations] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [placeSearch, setPlaceSearch] = useState("");
  const [placeCategoryFilter, setPlaceCategoryFilter] = useState("");
  const [activeTab, setActiveTab] = useState("basic");

  const emptyForm = {
    title: "", slug: "", description: "", meta_title: "", meta_description: "", canonical_url: "",
    featured_image: "", location_id: "", author_id: "", category: "general",
    place_ids: [], quick_info: [], sections: [], travel_tips: [],
    map_embed_url: "", show_in_blog: false, is_published: false,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchCollections(); fetchAllPlaces(); fetchLocations(); fetchAuthors(); }, []);

  const fetchCollections = async () => {
    try { const r = await seoCollectionAPI.getAll(token); setCollections(r.data); }
    catch { toast.error("Failed to fetch collections"); }
    finally { setLoading(false); }
  };
  const fetchAllPlaces = async () => { try { const r = await placeAPI.getAll({}); setAllPlaces(r.data); } catch {} };
  const fetchLocations = async () => { try { const r = await locationAPI.getAll(); setLocations(r.data); } catch {} };
  const fetchAuthors = async () => { try { const r = await authorsAPI.getAll(); setAuthors(r.data); } catch {} };

  const resetForm = () => { setForm(emptyForm); setEditing(null); setPlaceSearch(""); setPlaceCategoryFilter(""); setActiveTab("basic"); };

  const handleEdit = (col) => {
    setEditing(col);
    setForm({
      title: col.title || "", slug: col.slug || "", description: col.description || "",
      meta_title: col.meta_title || "", meta_description: col.meta_description || "", canonical_url: col.canonical_url || "",
      featured_image: col.featured_image || "", location_id: col.location_id || "",
      author_id: col.author_id || "", category: col.category || "general",
      place_ids: col.place_ids || [], quick_info: col.quick_info || [],
      sections: col.sections || [], travel_tips: col.travel_tips || [],
      map_embed_url: col.map_embed_url || "",
      show_in_blog: col.show_in_blog || false, is_published: col.is_published || false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Clean empty quick_info/sections
      payload.quick_info = (payload.quick_info || []).filter(q => q.label && q.value);
      payload.sections = (payload.sections || []).filter(s => s.title);
      payload.travel_tips = (payload.travel_tips || []).filter(t => t.trim());
      if (editing) {
        await seoCollectionAPI.update(editing.id, payload, token);
        toast.success("Collection updated");
      } else {
        await seoCollectionAPI.create(payload, token);
        toast.success("Collection created");
      }
      setShowModal(false); resetForm(); fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this SEO collection?")) return;
    try { await seoCollectionAPI.delete(id, token); toast.success("Deleted"); fetchCollections(); }
    catch { toast.error("Failed to delete"); }
  };

  const selectedPlaceIds = new Set(form.place_ids.map(p => p.place_id));
  const filteredPlaces = allPlaces.filter(p => {
    if (selectedPlaceIds.has(p.id)) return false;
    if (placeSearch && !p.name.toLowerCase().includes(placeSearch.toLowerCase())) return false;
    if (placeCategoryFilter && p.category !== placeCategoryFilter) return false;
    return true;
  });

  const addPlace = (placeId) => {
    const maxOrder = form.place_ids.length > 0 ? Math.max(...form.place_ids.map(p => p.order)) + 1 : 0;
    setForm({ ...form, place_ids: [...form.place_ids, { place_id: placeId, order: maxOrder }] });
  };
  const removePlace = (placeId) => setForm({ ...form, place_ids: form.place_ids.filter(p => p.place_id !== placeId) });
  const movePlace = (idx, dir) => {
    const items = [...form.place_ids];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    setForm({ ...form, place_ids: items.map((p, i) => ({ ...p, order: i })) });
  };
  const getPlaceById = useCallback((id) => allPlaces.find(p => p.id === id), [allPlaces]);
  const sortedSelectedPlaces = [...form.place_ids].sort((a, b) => a.order - b.order);

  // Quick Info helpers
  const addQuickInfo = () => setForm({ ...form, quick_info: [...form.quick_info, { label: "", value: "", icon: "info" }] });
  const updateQuickInfo = (idx, field, val) => {
    const items = [...form.quick_info];
    items[idx] = { ...items[idx], [field]: val };
    setForm({ ...form, quick_info: items });
  };
  const removeQuickInfo = (idx) => setForm({ ...form, quick_info: form.quick_info.filter((_, i) => i !== idx) });

  // Section helpers
  const addSection = () => setForm({ ...form, sections: [...form.sections, { title: "", slug: "", content: "", image: "", order: form.sections.length }] });
  const updateSection = (idx, field, val) => {
    const items = [...form.sections];
    items[idx] = { ...items[idx], [field]: val };
    if (field === "title" && !items[idx].slug) {
      items[idx].slug = val.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    }
    setForm({ ...form, sections: items });
  };
  const removeSection = (idx) => setForm({ ...form, sections: form.sections.filter((_, i) => i !== idx) });
  const moveSection = (idx, dir) => {
    const items = [...form.sections];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    setForm({ ...form, sections: items.map((s, i) => ({ ...s, order: i })) });
  };

  // Travel tip helpers
  const addTravelTip = () => setForm({ ...form, travel_tips: [...form.travel_tips, ""] });
  const updateTravelTip = (idx, val) => {
    const items = [...form.travel_tips];
    items[idx] = val;
    setForm({ ...form, travel_tips: items });
  };
  const removeTravelTip = (idx) => setForm({ ...form, travel_tips: form.travel_tips.filter((_, i) => i !== idx) });

  const handleCategoryChange = (newCat) => {
    const template = CATEGORY_TEMPLATES[newCat];
    if (template && !editing) {
      // Only auto-fill if creating new & fields are empty
      const updates = { category: newCat };
      if (form.quick_info.length === 0) updates.quick_info = template.quick_info;
      if (form.sections.length === 0) updates.sections = template.sections;
      if (form.travel_tips.length === 0) updates.travel_tips = template.travel_tips;
      if (Object.keys(updates).length > 1) {
        toast.success(`Applied ${newCat} template! Sections & tips pre-filled.`);
      }
      setForm({ ...form, ...updates });
    } else {
      setForm({ ...form, category: newCat });
    }
  };

  const TABS = [
    { key: "basic", label: "Basic Info" },
    { key: "content", label: "Content Sections" },
    { key: "places", label: "Places" },
    { key: "extras", label: "Tips & Map" },
    { key: "seo", label: "SEO" },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-seo-collections">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">Programmatic SEO</h1>
            <p className="text-muted-foreground">Create curated place collection pages for SEO</p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="rounded-full" data-testid="add-collection-btn">
            <Plus className="w-4 h-4 mr-2" />New Collection
          </Button>
        </div>

        {/* Listing */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : collections.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {collections.map((col) => (
              <div key={col.id} className="bg-white rounded-xl shadow-soft overflow-hidden" data-testid={`collection-${col.id}`}>
                {col.featured_image ? (
                  <div className="aspect-video overflow-hidden"><img src={col.featured_image} alt={col.title} className="w-full h-full object-cover" /></div>
                ) : (
                  <div className="aspect-video bg-muted/50 flex items-center justify-center"><Globe className="w-10 h-10 text-muted-foreground/40" /></div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-foreground text-sm leading-snug">{col.title}</h3>
                    <div className="flex gap-1 ml-2 flex-shrink-0">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full capitalize">{col.category || "general"}</span>
                      {col.is_published ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Live</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Draft</span>
                      )}
                    </div>
                  </div>
                  {col.location_name && <p className="text-xs text-muted-foreground mb-1"><MapPin className="w-3 h-3 inline" /> {col.location_name}</p>}
                  <p className="text-xs text-muted-foreground mb-3">{col.place_ids?.length || 0} places | {col.sections?.length || 0} sections | /{col.slug}</p>
                  <div className="flex items-center gap-2 mb-3">
                    <a href={`/guide/${col.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-coconut-green hover:underline flex items-center gap-1" data-testid={`view-col-${col.id}`}><ExternalLink className="w-3 h-3" /> View Live</a>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(col)} className="flex-1" data-testid={`edit-col-${col.id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(col.id)} className="text-red-500 hover:bg-red-50"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <Globe className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No SEO Collections Yet</h3>
            <p className="text-muted-foreground mb-6">Create curated pages like "Top Places To Visit In Alibaug" for programmatic SEO.</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }}><Plus className="w-4 h-4 mr-2" />Create First Collection</Button>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Collection" : "New SEO Collection"}</DialogTitle>
            </DialogHeader>

            {/* Tabs */}
            <div className="flex gap-1 bg-muted/40 rounded-lg p-1 mb-4">
              {TABS.map((tab) => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 text-xs font-medium py-2 px-3 rounded-md transition-colors ${activeTab === tab.key ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`tab-${tab.key}`}
                >{tab.label}</button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="collection-form">

              {/* ── BASIC TAB ── */}
              {activeTab === "basic" && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Title *</label>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Famous Temples in Maharashtra" data-testid="col-title" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Slug</label>
                      <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated" data-testid="col-slug" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Category</label>
                      <Select value={form.category} onValueChange={handleCategoryChange}>
                        <SelectTrigger data-testid="col-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {GUIDE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Location</label>
                      <Select value={form.location_id || "none"} onValueChange={(v) => setForm({ ...form, location_id: v === "none" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Author</label>
                    <Select value={form.author_id || "none"} onValueChange={(v) => setForm({ ...form, author_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="col-author"><SelectValue placeholder="Select author" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Author</SelectItem>
                        {authors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <ImageUpload value={form.featured_image} onChange={(url) => setForm({ ...form, featured_image: url })} token={token} label="Featured Image" />
                  <div>
                    <label className="text-sm font-medium mb-1 block">Overview / Introduction</label>
                    <RichTextEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="Write the guide introduction..." minHeight="100px" />
                  </div>

                  {/* Quick Info Cards */}
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold flex items-center gap-1"><LayoutList className="w-4 h-4" /> Quick Info Cards</h4>
                      <Button type="button" variant="outline" size="sm" onClick={addQuickInfo}><Plus className="w-3 h-3 mr-1" />Add Card</Button>
                    </div>
                    {form.quick_info.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 bg-muted/20 rounded-lg p-2">
                        <Select value={item.icon} onValueChange={(v) => updateQuickInfo(i, "icon", v)}>
                          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>{ICON_OPTIONS.map(ic => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={item.label} onChange={(e) => updateQuickInfo(i, "label", e.target.value)} placeholder="Label" className="w-32" />
                        <Input value={item.value} onChange={(e) => updateQuickInfo(i, "value", e.target.value)} placeholder="Value" className="flex-1" />
                        <button type="button" onClick={() => removeQuickInfo(i)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>

                  {/* Toggles */}
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.show_in_blog} onChange={(e) => setForm({ ...form, show_in_blog: e.target.checked })} className="rounded border-gray-300" data-testid="col-show-blog" />
                      <span className="text-sm">Show in Blog</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="rounded border-gray-300" data-testid="col-published" />
                      <span className="text-sm">Published (Live)</span>
                    </label>
                  </div>
                </div>
              )}

              {/* ── CONTENT SECTIONS TAB ── */}
              {activeTab === "content" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold">Content Sections</h4>
                    <Button type="button" variant="outline" size="sm" onClick={addSection}><Plus className="w-3 h-3 mr-1" />Add Section</Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Add custom content sections like History, Architecture, Darshan Timings, etc.</p>
                  {form.sections.map((section, i) => (
                    <div key={i} className="border rounded-xl p-4 space-y-3 bg-white">
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-muted rounded px-1.5 py-0.5">{i + 1}</span>
                        <Input value={section.title} onChange={(e) => updateSection(i, "title", e.target.value)} placeholder="Section Title (e.g., History)" className="flex-1" />
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1 hover:bg-muted rounded disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                          <button type="button" onClick={() => moveSection(i, 1)} disabled={i === form.sections.length - 1} className="p-1 hover:bg-muted rounded disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                          <button type="button" onClick={() => removeSection(i)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <RichTextEditor value={section.content} onChange={(v) => updateSection(i, "content", v)} placeholder="Write section content..." minHeight="80px" />
                      <ImageUpload value={section.image} onChange={(url) => updateSection(i, "image", url)} token={token} label="Section Image (optional)" />
                    </div>
                  ))}
                  {form.sections.length === 0 && (
                    <div className="text-center py-8 bg-muted/20 rounded-xl">
                      <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No sections yet. Add content sections to make your guide more comprehensive.</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── PLACES TAB ── */}
              {activeTab === "places" && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold">Places in this Collection ({sortedSelectedPlaces.length})</h4>
                  {sortedSelectedPlaces.length > 0 && (
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {sortedSelectedPlaces.map((item, idx) => {
                        const place = getPlaceById(item.place_id);
                        if (!place) return null;
                        return (
                          <div key={item.place_id} className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2" data-testid={`selected-place-${item.place_id}`}>
                            <GripVertical className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
                            <span className="text-xs bg-muted rounded px-1.5 py-0.5 flex-shrink-0">{idx + 1}</span>
                            {place.image && <img src={place.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{place.name}</p>
                              <p className="text-xs text-muted-foreground">{categoryIcons[place.category]} {place.category}{place.location_name ? ` · ${place.location_name}` : ""}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button type="button" onClick={() => movePlace(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-muted rounded disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                              <button type="button" onClick={() => movePlace(idx, 1)} disabled={idx === sortedSelectedPlaces.length - 1} className="p-1 hover:bg-muted rounded disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                              <button type="button" onClick={() => removePlace(item.place_id)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="w-3 h-3" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="border-t pt-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Add places:</p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input value={placeSearch} onChange={(e) => setPlaceSearch(e.target.value)} placeholder="Search places..." className="pl-9" data-testid="place-search" />
                      </div>
                      <Select value={placeCategoryFilter || "all"} onValueChange={(v) => setPlaceCategoryFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-36"><SelectValue placeholder="Category" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {["Beaches", "Temples", "Forts", "Waterfalls", "Restaurants", "Parks"].map(c => <SelectItem key={c} value={c}>{categoryIcons[c]} {c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {filteredPlaces.length > 0 ? filteredPlaces.slice(0, 20).map(place => (
                        <button key={place.id} type="button" onClick={() => addPlace(place.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-primary/5 border border-transparent hover:border-primary/20 transition-colors text-left"
                          data-testid={`add-place-${place.id}`}>
                          {place.image ? <img src={place.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" /> : <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0"><MapPin className="w-3 h-3 text-muted-foreground" /></div>}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{place.name}</p>
                            <p className="text-xs text-muted-foreground">{categoryIcons[place.category]} {place.category}{place.location_name ? ` · ${place.location_name}` : ""}</p>
                          </div>
                          <Plus className="w-4 h-4 text-primary flex-shrink-0" />
                        </button>
                      )) : (
                        <p className="text-sm text-muted-foreground text-center py-4">No matching places found</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── TIPS & MAP TAB ── */}
              {activeTab === "extras" && (
                <div className="space-y-5">
                  {/* Map */}
                  <div>
                    <label className="text-sm font-medium mb-1 block flex items-center gap-1"><Map className="w-4 h-4" /> Google Maps Embed URL</label>
                    <Input value={form.map_embed_url} onChange={(e) => setForm({ ...form, map_embed_url: e.target.value })} placeholder="https://www.google.com/maps/embed?pb=..." data-testid="col-map-url" />
                    <p className="text-xs text-muted-foreground mt-1">Paste a Google Maps embed URL. Get it from Google Maps → Share → Embed a map.</p>
                  </div>

                  {/* Travel Tips */}
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold flex items-center gap-1"><Lightbulb className="w-4 h-4" /> Travel Tips</h4>
                      <Button type="button" variant="outline" size="sm" onClick={addTravelTip}><Plus className="w-3 h-3 mr-1" />Add Tip</Button>
                    </div>
                    {form.travel_tips.map((tip, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input value={tip} onChange={(e) => updateTravelTip(i, e.target.value)} placeholder="e.g., Visit early morning to avoid crowds" className="flex-1" />
                        <button type="button" onClick={() => removeTravelTip(i)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── SEO TAB ── */}
              {activeTab === "seo" && (
                <div className="space-y-4">
                  <div className="border rounded-xl p-4 bg-muted/20 space-y-3">
                    <h4 className="text-sm font-semibold flex items-center gap-1"><FileText className="w-4 h-4" /> SEO Meta</h4>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Meta Title</label>
                      <Input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} placeholder="SEO page title" data-testid="col-meta-title" />
                      <p className="text-xs text-muted-foreground mt-0.5">{form.meta_title?.length || 0}/60</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Meta Description</label>
                      <textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} placeholder="SEO page description" rows={2}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="col-meta-desc" />
                      <p className="text-xs text-muted-foreground mt-0.5">{form.meta_description?.length || 0}/160</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Canonical URL</label>
                      <Input value={form.canonical_url} onChange={(e) => setForm({ ...form, canonical_url: e.target.value })} placeholder="https://kokanhomestays.in/guide/... (leave empty for auto)" data-testid="col-canonical-url" />
                      <p className="text-xs text-muted-foreground mt-0.5">Leave blank to use the default page URL</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="col-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editing ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSeoCollections;
